import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    mode: 'light',
    // Primary UI elements - Deep Blue / Muted Teal
    primary: {
      main: '#1A3E5F', // Deep Blue
      light: '#2F6C75', // Muted Teal
      dark: '#10263A',
      contrastText: '#ffffff',
    },
    // Secondary accents - Muted Teal / Soft Sea Green
    secondary: {
      main: '#2F6C75', // Muted Teal
      light: '#7BAA9E', // Soft Sea Green
      dark: '#234950',
      contrastText: '#ffffff',
    },
    // Muted Gold - Used for alerts and emphasis
    warning: {
      main: '#C5A25A', // Muted Gold
      light: '#D4B878',
      dark: '#9E7F41',
      contrastText: '#ffffff',
    },
    // Backgrounds / cards - soft aquas and sands
    background: {
      default: '#F5F7F6', // soft neutral base
      paper: '#FFFFFF',   // main card surface
    },
    text: {
      primary: '#4E5D61',   // Slate Gray - grounding text
      secondary: '#6B7A84', // softer body text
      disabled: '#A0ADB5',
    },
    divider: '#DEE2E6',
    // Custom slate shades
    slate: {
      50: '#F5F7F6',
      100: '#E4DCCF',    // Warm Sand
      200: '#DEE2E6',
      300: '#C5E4E1',    // Pale Aqua
      400: '#A0ADB5',
      500: '#6B7A84',
      600: '#4E5D61',    // Slate Gray
      700: '#3C4A4F',
      800: '#27333A',
      900: '#1A3E5F',    // Deep Blue
    },
    // Extended palette based on the new scheme
    ocean: {
      deep: '#1A3E5F',      // Deep Blue
      medium: '#2F6C75',    // Muted Teal
      light: '#7BAA9E',     // Soft Sea Green
    },
    // Earth / accent tones
    earth: {
      warmSand: '#E4DCCF',
      coralMist: '#E6A57E',
      mutedGold: '#C5A25A',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
    // Hero heading
    h1: { 
      fontWeight: 600, 
      letterSpacing: '-0.025em',
      fontSize: 'clamp(2.5rem, 5vw, 3.75rem)', // text-4xl to text-5xl
      lineHeight: 1.1,
    },
    // Section heading
    h2: { 
      fontWeight: 600,
      letterSpacing: '-0.025em',
      fontSize: 'clamp(1.5rem, 3.5vw, 1.875rem)', // text-xl to text-2xl
      lineHeight: 1.2,
    },
    h3: { 
      fontWeight: 600,
      letterSpacing: '-0.02em',
      fontSize: 'clamp(1.25rem, 3vw, 1.5rem)', // text-lg to text-xl
      lineHeight: 1.3,
    },
    h4: { 
      fontWeight: 600,
      fontSize: '1.125rem', // text-lg
      lineHeight: 1.4,
    },
    h5: { 
      fontWeight: 600,
      fontSize: '1rem', // text-base
      lineHeight: 1.4,
    },
    h6: { 
      fontWeight: 600,
      fontSize: '0.875rem', // text-sm
      lineHeight: 1.4,
    },
    // Body text - 14px default for information density
    body1: { 
      fontSize: '0.875rem', // text-sm (14px)
      lineHeight: 1.6,
    },
    body2: { 
      fontSize: '0.875rem', // text-sm
      lineHeight: 1.5,
      color: '#495057',
    },
    // Eyebrow text - labels above headings
    overline: {
      fontSize: '0.75rem', // text-xs
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      color: '#64748B', // slate-500
      lineHeight: 1.4,
    },
    caption: {
      fontSize: '0.75rem', // text-xs
      lineHeight: 1.4,
      color: '#64748B',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '0.875rem',
    },
  },
  shape: {
    borderRadius: 12, // rounded-lg (8-12px)
  },
  shadows: [
    'none', // 0
    '0 1px 2px 0 rgba(0, 0, 0, 0.05)', // 1 - shadow-sm
    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)', // 2 - shadow
    '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', // 3 - shadow-md
    '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // 4 - shadow-lg
    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', // 5 - shadow-xl
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 6 - shadow-2xl
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 7
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 8
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 9
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 10
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 11
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 12
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 13
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 14
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 15
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 16
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 17
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 18
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 19
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 20
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 21
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 22
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 23
    '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // 24
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F5F7F6',
          minHeight: '100vh',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 20px',
          transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        contained: {
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          border: '1px solid #DEE2E6',
          borderRadius: 12,
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          transition: 'box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        },
        elevation2: {
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
        elevation3: {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid #DEE2E6',
          boxShadow: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#FFFFFF',
            borderRadius: 8,
            '& fieldset': {
              borderColor: '#CED4DA',
            },
            '&:hover fieldset': {
              borderColor: '#6B7A84',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#2F6C75',
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: '#FFFFFF',
          borderRadius: 8,
        },
      },
    },
  },
});

// Add custom theme extensions for Modern Scientific design system
theme.custom = {
  // Maximum width for content containers (1280px)
  maxWidth: '80rem',
  // Responsive padding
  containerPadding: {
    xs: '1rem',
    sm: '1.5rem',
    lg: '2rem',
    },
  // CCVA Card - The most common building block
  ccvaCard: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E4DCCF',
      borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    transition: 'box-shadow 200ms cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
  },
  // Eyebrow text style
  eyebrow: {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    color: '#6B7A84',
  },
  // Gradients for hero sections
  gradients: {
    heroDark: 'linear-gradient(135deg, #1A3E5F 0%, #2F6C75 100%)',
    oceanDeep: 'linear-gradient(135deg, #1A3E5F 0%, #2F6C75 100%)',
  },
};

export default theme; 